//src/components/milestone/EvidenceConfigModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Save, FileText, Image, Video, File, Folder } from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  getEvidenceDefinitions, 
  addEvidenceDefinition, 
  updateEvidenceDefinition, 
  deleteEvidenceDefinition 
} from '../../services/milestoneService';

/**
 * Build hierarchical tree from flat evidence list
 */
const buildEvidenceTree = (flatList) => {
  if (!flatList || flatList.length === 0) {
    return [];
  }

  // Create a map for quick lookup
  const evidenceMap = new Map();
  const rootNodes = [];

  // First pass: Create map and identify roots
  flatList.forEach(evidence => {
    evidenceMap.set(evidence.id, {
      ...evidence,
      children: []
    });

    if (!evidence.parentId) {
      rootNodes.push(evidence.id);
    }
  });

  // Second pass: Build parent-child relationships
  flatList.forEach(evidence => {
    if (evidence.parentId) {
      const parent = evidenceMap.get(evidence.parentId);
      const child = evidenceMap.get(evidence.id);
      
      if (parent && child) {
        parent.children.push(child);
      }
    }
  });

  // Sort children by displayOrder
  evidenceMap.forEach(evidence => {
    if (evidence.children.length > 0) {
      evidence.children.sort((a, b) => 
        (a.displayOrder || 0) - (b.displayOrder || 0)
      );
    }
  });

  // Get root nodes and sort them
  const tree = rootNodes
    .map(id => evidenceMap.get(id))
    .filter(Boolean)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return tree;
};

/**
 * Flatten tree to list (preserving hierarchy order)
 */
const flattenEvidenceTree = (tree, result = []) => {
  tree.forEach(node => {
    // Add node to result
    result.push(node);
    
    // Recursively add children
    if (node.children && node.children.length > 0) {
      flattenEvidenceTree(node.children, result);
    }
  });
  
  return result;
};

/**
 * Generate hierarchical number (e.g., "1.2", "2.1.1")
 */
const getHierarchicalNumber = (evidenceList, evidence) => {
  // Root level (no parent)
  if (!evidence.parentId) {
    const rootItems = evidenceList.filter(e => !e.parentId);
    const index = rootItems.findIndex(e => e.id === evidence.id);
    return `${index + 1}`;
  }
  
  // Child level (has parent)
  const parent = evidenceList.find(e => e.id === evidence.parentId);
  if (!parent) return '?';
  
  // Get parent's number recursively
  const parentNumber = getHierarchicalNumber(evidenceList, parent);
  
  // Get position among siblings
  const siblings = evidenceList.filter(e => e.parentId === evidence.parentId);
  const childIndex = siblings.findIndex(e => e.id === evidence.id);
  
  return `${parentNumber}.${childIndex + 1}`;
};

/**
 * EvidenceConfigModal Component - WITH HIERARCHY SUPPORT
 * ✅ Visual distinction for groups (yellow background)
 * ✅ "Add Child" button on groups
 * ✅ Parent selection with info banner
 * ✅ Indentation for hierarchy levels
 * 
 * Props:
 * - isOpen: Boolean to show/hide modal
 * - onClose: Callback when modal closes
 * - milestone: Milestone template object
 * - onSave: Callback after successful save
 */
const EvidenceConfigModal = ({ isOpen, onClose, milestone, onSave }) => {
  const [evidenceList, setEvidenceList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // ✅ UPDATED: Form state with hierarchy fields
  const [formData, setFormData] = useState({
    evidenceName: '',
    evidenceType: 'PHOTO',
    isGroup: false,
    parentId: null,
    evidenceCode: '',
    isMandatory: true,
    minFileCount: 1,
    maxFileCount: 5,
    allowedFileTypes: '',
    maxFileSizeMb: 10,
    description: '',
    displayOrder: 1
  });

  // Load evidence definitions when modal opens
  useEffect(() => {
    if (isOpen && milestone) {
      loadEvidenceDefinitions();
    }
  }, [isOpen, milestone]);

    const loadEvidenceDefinitions = async () => {
    try {
        setLoading(true);
        const data = await getEvidenceDefinitions(milestone.id);
        
        // ✅ BUILD TREE: Convert flat list to hierarchical structure
        const tree = buildEvidenceTree(data);
        
        // ✅ FLATTEN: Convert tree back to flat list in correct order
        const orderedList = flattenEvidenceTree(tree);
        
        setEvidenceList(orderedList);
    } catch (error) {
        console.error('Error loading evidence definitions:', error);
        toast.error('Gagal memuat evidence definitions');
    } finally {
        setLoading(false);
    }
    };

  // ✅ UPDATED: Handle form input change with isGroup logic
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for isGroup checkbox
    if (name === 'isGroup') {
      setFormData(prev => ({
        ...prev,
        isGroup: checked,
        // Clear file constraints when becoming a group
        ...(checked && {
          minFileCount: null,
          maxFileCount: null,
          allowedFileTypes: '',
          maxFileSizeMb: null
        })
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : 
                (type === 'number' && value === '' ? null : value)
      }));
    }
  };

  // Start adding new evidence (root level)
  const startAddNew = () => {
    setIsEditing(true);
    setEditingId(null);
    setFormData({
      evidenceName: '',
      evidenceType: 'PHOTO',
      isGroup: false,
      parentId: null,
      evidenceCode: '',
      isMandatory: true,
      minFileCount: 1,
      maxFileCount: 5,
      allowedFileTypes: '',
      maxFileSizeMb: 10,
      description: '',
      displayOrder: evidenceList.length + 1
    });
  };

  // ✅ NEW: Start adding child evidence under a parent
    const startAddChild = (parentEvidence) => {
    // ✅ Calculate next display order for children
    const siblingChildren = evidenceList.filter(e => e.parentId === parentEvidence.id);
    const maxChildOrder = siblingChildren.length > 0 
        ? Math.max(...siblingChildren.map(e => e.displayOrder || 0))
        : parentEvidence.displayOrder;
    
    setIsEditing(true);
    setEditingId(null);
    setFormData({
        evidenceName: '',
        evidenceType: 'PHOTO',
        isGroup: false,
        parentId: parentEvidence.id,
        evidenceCode: '',
        isMandatory: true,
        minFileCount: 1,
        maxFileCount: 5,
        allowedFileTypes: '',
        maxFileSizeMb: 10,
        description: '',
        displayOrder: maxChildOrder + 1  // ✅ Smart ordering
    });
    };

  // ✅ UPDATED: Start editing with hierarchy fields
  const startEdit = (evidence) => {
    setIsEditing(true);
    setEditingId(evidence.id);
    setFormData({
      evidenceName: evidence.evidenceName,
      evidenceType: evidence.evidenceType,
      isGroup: evidence.isGroup || false,
      parentId: evidence.parentId || null,
      evidenceCode: evidence.evidenceCode || '',
      isMandatory: evidence.isMandatory,
      minFileCount: evidence.minFileCount,
      maxFileCount: evidence.maxFileCount,
      allowedFileTypes: evidence.allowedFileTypes || '',
      maxFileSizeMb: evidence.maxFileSizeMb,
      description: evidence.description || '',
      displayOrder: evidence.displayOrder
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      evidenceName: '',
      evidenceType: 'PHOTO',
      isGroup: false,
      parentId: null,
      evidenceCode: '',
      isMandatory: true,
      minFileCount: 1,
      maxFileCount: 5,
      allowedFileTypes: '',
      maxFileSizeMb: 10,
      description: '',
      displayOrder: 1
    });
  };

  // ✅ UPDATED: Save with hierarchy validation
  const saveEvidence = async () => {
    try {
      // Validation
      if (!formData.evidenceName.trim()) {
        toast.error('Evidence name is required');
        return;
      }

      // Validate groups don't need file constraints
      if (formData.isGroup) {
        formData.minFileCount = null;
        formData.maxFileCount = null;
        formData.allowedFileTypes = null;
        formData.maxFileSizeMb = null;
      } else {
        // Leaf evidence needs file types
        if (!formData.allowedFileTypes || !formData.allowedFileTypes.trim()) {
          toast.error('Allowed file types are required for non-group evidence');
          return;
        }
      }

      // Validate level (max 3 levels)
      if (formData.parentId) {
        const parent = evidenceList.find(e => e.id === parseInt(formData.parentId));
        if (parent) {
          const parentLevel = parent.level || 0;
          if (parentLevel >= 2) {
            toast.error('Cannot add evidence more than 3 levels deep (max level 2)');
            return;
          }
          if (formData.isGroup && parentLevel >= 1) {
            toast.error('Groups can only be at level 0 or 1 (level 2 must be leaf evidence)');
            return;
          }
        }
      }

      setLoading(true);

      // Prepare payload
      const payload = {
        ...formData,
        templateId: milestone.id
      };

      if (editingId) {
        // Update existing
        await updateEvidenceDefinition(milestone.id, editingId, payload);
        toast.success('Evidence requirement updated successfully');
      } else {
        // Add new
        await addEvidenceDefinition(milestone.id, payload);
        toast.success('Evidence requirement added successfully');
      }

      // Reload list
      await loadEvidenceDefinitions();
      cancelEdit();
      
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving evidence definition:', error);
      toast.error(error.response?.data?.message || 'Failed to save evidence requirement');
    } finally {
      setLoading(false);
    }
  };

  // Delete evidence definition
  const deleteEvidence = async (id) => {
    if (!window.confirm('Are you sure you want to delete this evidence requirement?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteEvidenceDefinition(milestone.id, id);
      toast.success('Evidence requirement deleted successfully');
      await loadEvidenceDefinitions();
      
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error deleting evidence definition:', error);
      toast.error('Failed to delete evidence requirement');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Get available parent options (groups only, max level 1)
  const getParentOptions = () => {
    return evidenceList.filter(e => 
      e.isGroup && 
      (!e.level || e.level < 2) &&
      e.id !== editingId
    );
  };

  // Get icon based on evidence type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'PHOTO':
        return <Image className="w-5 h-5 text-blue-600" />;
      case 'VIDEO':
        return <Video className="w-5 h-5 text-purple-600" />;
      case 'DOCUMENT':
        return <FileText className="w-5 h-5 text-green-600" />;
      default:
        return <File className="w-5 h-5 text-gray-600" />;
    }
  };

  // Get suggested file types based on evidence type
  const getSuggestedFileTypes = (type) => {
    switch (type) {
      case 'PHOTO':
        return 'jpg,jpeg,png,gif,webp';
      case 'VIDEO':
        return 'mp4,avi,mov,mkv';
      case 'DOCUMENT':
        return 'pdf,doc,docx,xls,xlsx';
      default:
        return '';
    }
  };

  // Auto-fill file types when evidence type changes (only for non-groups)
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      evidenceType: newType,
      allowedFileTypes: prev.isGroup ? '' : getSuggestedFileTypes(newType)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-red-600">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">⚙️ Configure Evidence Requirements</h2>
              <p className="text-sm text-blue-100 mt-1">
                Milestone: <span className="font-semibold">{milestone?.itemCode} - {milestone?.milestoneName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Button */}
          {!isEditing && (
            <div className="mb-6">
              <button
                onClick={startAddNew}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                disabled={loading}
              >
                <Plus className="w-5 h-5" />
                <span>Add Evidence Requirement</span>
              </button>
            </div>
          )}

          {/* Form (Add/Edit) */}
          {isEditing && (
            <div className="mb-6 p-4 border-2 border-blue-300 rounded-lg bg-blue-50">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingId ? 'Edit Evidence Requirement' : 'New Evidence Requirement'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Evidence Name */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Evidence Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="evidenceName"
                    value={formData.evidenceName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={formData.isGroup ? "e.g., Foto Tower, Dokumentasi BTS" : "e.g., Foto Tray Vertical, Document ATP"}
                  />
                </div>

                {/* ✅ NEW: Parent Info Banner (if parentId is set) */}
                {formData.parentId && (
                  <div className="col-span-2 p-3 bg-blue-50 border-2 border-blue-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Folder className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            Child of: <strong>{evidenceList.find(e => e.id === formData.parentId)?.evidenceName}</strong>
                          </p>
                          <p className="text-xs text-blue-700">
                            This evidence will be nested under the parent group (Level {(evidenceList.find(e => e.id === formData.parentId)?.level || 0) + 1})
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, parentId: null }))}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Remove parent"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* ✅ NEW: Is Group Checkbox */}
                <div className="col-span-2">
                  <div className="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <input
                      type="checkbox"
                      name="isGroup"
                      checked={formData.isGroup}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <div className="flex items-center space-x-2">
                      <Folder className="w-5 h-5 text-yellow-600" />
                      <label className="text-sm font-medium text-gray-900">
                        Is Group/Folder (for organizing related evidence)
                      </label>
                    </div>
                  </div>
                  {formData.isGroup && (
                    <p className="text-xs text-yellow-700 mt-1 ml-1">
                      ℹ️ Groups organize evidence but don't require file uploads themselves
                    </p>
                  )}
                </div>

                {/* ✅ NEW: Parent Selection (only show if NOT already set and has groups) */}
                {!formData.parentId && getParentOptions().length > 0 && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Group (optional)
                    </label>
                    <select
                      name="parentId"
                      value={formData.parentId || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">-- No Parent (Root Level) --</option>
                      {getParentOptions().map(parent => (
                        <option key={parent.id} value={parent.id}>
                          📁 {parent.evidenceName} (Level {parent.level || 0})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Tip: Click "Add Child" button on a group to auto-select parent
                    </p>
                  </div>
                )}

                {/* Evidence Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Evidence Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="evidenceType"
                    value={formData.evidenceType}
                    onChange={handleTypeChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="PHOTO">Photo</option>
                    <option value="DOCUMENT">Document</option>
                    <option value="VIDEO">Video</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {/* Mandatory */}
                <div className="flex items-center space-x-2 pt-8">
                  <input
                    type="checkbox"
                    name="isMandatory"
                    checked={formData.isMandatory}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Mandatory Evidence
                  </label>
                </div>

                {/* ✅ FILE CONSTRAINTS: Only show for non-groups */}
                {!formData.isGroup && (
                  <>
                    {/* Min File Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min File Count
                      </label>
                      <input
                        type="number"
                        name="minFileCount"
                        value={formData.minFileCount || ''}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Max File Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max File Count
                      </label>
                      <input
                        type="number"
                        name="maxFileCount"
                        value={formData.maxFileCount || ''}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Allowed File Types */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Allowed File Types <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="allowedFileTypes"
                        value={formData.allowedFileTypes}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., jpg,jpeg,png,pdf"
                      />
                      <p className="text-xs text-gray-500 mt-1">Comma-separated file extensions</p>
                    </div>

                    {/* Max File Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max File Size (MB)
                      </label>
                      <input
                        type="number"
                        name="maxFileSizeMb"
                        value={formData.maxFileSizeMb || ''}
                        onChange={handleInputChange}
                        min="1"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}

                {/* Description */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={formData.isGroup ? "Describe this group of evidence..." : "Describe what this evidence should contain..."}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={saveEvidence}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                  disabled={loading}
                >
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Saving...' : 'Save'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Evidence List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Evidence Requirements ({evidenceList.length})
            </h3>

            {loading && evidenceList.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Loading evidence requirements...
              </div>
            ) : evidenceList.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <p className="text-gray-500">No evidence requirements configured yet.</p>
                <p className="text-sm text-gray-400 mt-1">Click "Add Evidence Requirement" to get started.</p>
              </div>
            ) : (
              evidenceList.map((evidence, index) => (
                <div
                  key={evidence.id}
                  className={`p-4 border rounded-lg transition-shadow ${
                    evidence.isGroup 
                      ? 'border-yellow-400 bg-yellow-50' 
                      : 'border-gray-200 bg-white hover:shadow-md'
                  }`}
                  style={{ marginLeft: `${(evidence.level || 0) * 24}px` }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* ✅ IMPROVED: Icon */}
                      <div className="mt-1">
                        {evidence.isGroup ? (
                          <div className="p-2 bg-yellow-200 rounded-lg">
                            <Folder className="w-6 h-6 text-yellow-700" />
                          </div>
                        ) : (
                          getTypeIcon(evidence.evidenceType)
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 flex-wrap gap-1">
                          <h4 className="font-semibold text-gray-900">
                            {getHierarchicalNumber(evidenceList, evidence)}. {evidence.evidenceName}
                          </h4>
                          
                          {/* ✅ Group badge */}
                          {evidence.isGroup && (
                            <span className="px-2 py-1 bg-yellow-200 text-yellow-900 text-xs font-semibold rounded-md flex items-center space-x-1 border border-yellow-400">
                              <Folder className="w-3 h-3" />
                              <span>GROUP</span>
                            </span>
                          )}
                          
                          {/* Mandatory badge */}
                          {evidence.isMandatory && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-md border border-red-300">
                              MANDATORY
                            </span>
                          )}
                          
                          {/* ✅ Level badge */}
                          {evidence.level > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-md border border-blue-300">
                              L{evidence.level}
                            </span>
                          )}
                        </div>

                        {/* ✅ IMPROVED: Details */}
                        <div className="mt-2 text-sm">
                          {evidence.isGroup ? (
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2 text-yellow-800 font-medium">
                                <Folder className="w-4 h-4" />
                                <span>Group Evidence (Organizer)</span>
                              </div>
                              <p className="text-gray-600">
                                Type: {evidence.evidenceType}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-gray-700">
                                <span className="font-medium">Type:</span> {evidence.evidenceType} • 
                                <span className="font-medium"> Files:</span> {evidence.minFileCount || 0}-{evidence.maxFileCount || '∞'}
                              </p>
                              {evidence.allowedFileTypes && (
                                <p className="text-gray-600">
                                  <span className="font-medium">Allowed:</span> {evidence.allowedFileTypes} • 
                                  <span className="font-medium">Max:</span> {evidence.maxFileSizeMb} MB
                                </p>
                              )}
                            </div>
                          )}
                          
                          {evidence.description && (
                            <p className="text-gray-500 italic mt-1 text-xs border-l-2 border-gray-300 pl-2">
                              {evidence.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ✅ IMPROVED: Actions with Add Child button */}
                    <div className="flex items-center space-x-1 ml-4">
                      {/* ✅ NEW: Add Child button (only for groups at level 0 or 1) */}
                      {evidence.isGroup && (!evidence.level || evidence.level < 2) && (
                        <button
                          onClick={() => startAddChild(evidence)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center space-x-1"
                          title="Add child evidence"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Edit button */}
                      <button
                        onClick={() => startEdit(evidence)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      
                      {/* Delete button */}
                      <button
                        onClick={() => deleteEvidence(evidence.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvidenceConfigModal;